import React, { useState, KeyboardEvent, useRef, useEffect } from "react";
import {
  Box,
  Textarea,
  IconButton,
  Flex,
  useColorModeValue,
  VStack,
  Collapse,
} from "@chakra-ui/react";
import { MdSend, MdAttachFile } from "react-icons/md";
import FileUpload from "./FileUpload";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  pendingFiles?: File[];
  isUploadingFile?: boolean;
  onFileUpload?: (file: File) => Promise<void>;
  onRemoveFile?: (fileId: string) => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder = "Type your message...",
  pendingFiles = [],
  isUploadingFile = false,
  onFileUpload,
  onRemoveFile,
}) => {
  const [message, setMessage] = useState("");
  const [showFileUpload, setShowFileUpload] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120;
      const minHeight = 44;

      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [message]);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage("");
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = "44px";
        }
        setShowFileUpload(false);
      }, 100);
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const handleFileUploadWrapper = async (file: File) => {
    if (onFileUpload) {
      await onFileUpload(file);
    }
  };

  const handleRemoveFileWrapper = (fileId: string) => {
    if (onRemoveFile) {
      onRemoveFile(fileId);
    }
  };

  const canShowAttachButton = Boolean(onFileUpload && onRemoveFile);

  return (
    <Box
      position="sticky"
      bottom={0}
      bg={bgColor}
      borderTop="1px solid"
      borderColor={borderColor}
      p={4}
    >
      <VStack spacing={3} align="stretch">
        {canShowAttachButton && (
          <Collapse in={showFileUpload} animateOpacity>
            <FileUpload
              pendingFiles={pendingFiles || []}
              isUploadingFile={isUploadingFile || false}
              onFileUpload={handleFileUploadWrapper}
              onRemoveFile={handleRemoveFileWrapper}
              disabled={disabled}
            />
          </Collapse>
        )}

        <Flex gap={2} align="flex-end">
          {canShowAttachButton && (
            <IconButton
              aria-label="Attach file"
              icon={<MdAttachFile />}
              variant="ghost"
              size="md"
              minWidth="44px"
              height="44px"
              colorScheme={showFileUpload ? "blue" : "gray"}
              onClick={() => setShowFileUpload(!showFileUpload)}
              disabled={disabled}
              _disabled={{
                opacity: 0.4,
                cursor: "not-allowed",
              }}
            />
          )}

          <Textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            flex={1}
            bg={useColorModeValue("gray.50", "gray.700")}
            border="1px solid"
            borderColor={borderColor}
            resize="none"
            rows={1}
            minHeight="44px"
            maxHeight="120px"
            height="44px"
            overflowY="auto"
            transition="height 0.1s ease"
            _focus={{
              borderColor: "blue.500",
              bg: useColorModeValue("white", "gray.600"),
            }}
            _disabled={{
              opacity: 0.6,
              cursor: "not-allowed",
            }}
            sx={{
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: useColorModeValue("gray.300", "gray.600"),
                borderRadius: "24px",
              },
            }}
          />

          <IconButton
            aria-label="Send message"
            icon={<MdSend />}
            onClick={handleSend}
            disabled={disabled || !message.trim()}
            colorScheme="blue"
            size="md"
            minWidth="44px"
            height="44px"
            _disabled={{
              opacity: 0.4,
              cursor: "not-allowed",
            }}
          />
        </Flex>
      </VStack>
    </Box>
  );
};

export default ChatInput;
